import { Link } from "react-router-dom";
import { 
  Shield, 
  CheckCircle, 
  Phone,
  FileText,
  Camera,
  Users,
  ArrowRight,
  ExternalLink,
  Zap,
  AlertTriangle,
  Clock,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

// Carrier data configuration
const CARRIER_DATA = {
  "state-farm": {
    name: "State Farm",
    claimsUrl: "https://www.statefarm.com/claims",
    claimsPhone: "1-800-732-5246",
    description: "State Farm is one of the largest insurance providers in Minnesota, known for their local agent network and comprehensive coverage options.",
    tips: [
      "State Farm typically requires photos of all damaged areas before approving claims",
      "Their mobile app allows you to file claims and upload photos directly",
      "State Farm often uses their own preferred vendors but you can choose your own contractor",
      "Request a copy of the Xactimate estimate from your adjuster"
    ]
  },
  "american-family": {
    name: "American Family",
    claimsUrl: "https://www.amfam.com/claims",
    claimsPhone: "1-800-692-6326",
    description: "American Family Insurance is headquartered in Madison, WI and serves many Minnesota homeowners with their DreamSecure homeowner policies.",
    tips: [
      "American Family's MyAmFam app streamlines the claims filing process",
      "They often provide quick claim decisions for straightforward storm damage",
      "Keep your agent informed throughout the claims process",
      "American Family offers replacement cost coverage on most policies"
    ]
  },
  "allstate": {
    name: "Allstate",
    claimsUrl: "https://www.allstate.com/claims",
    claimsPhone: "1-800-255-7828",
    description: "Allstate offers various homeowner policy options in Minnesota, with both standard and premier coverage levels.",
    tips: [
      "Allstate's QuickFoto Claim lets you submit photos for faster processing",
      "They have a network of preferred contractors but you're not required to use them",
      "Ask about their Claim RateGuard feature to protect your rates",
      "Allstate typically sends adjusters quickly after major Minnesota storms"
    ]
  },
  "farmers": {
    name: "Farmers Insurance",
    claimsUrl: "https://www.farmers.com/claims/",
    claimsPhone: "1-800-435-7764",
    description: "Farmers Insurance provides comprehensive homeowner coverage in Minnesota with flexible policy options.",
    tips: [
      "Farmers offers a video chat option for initial damage assessment",
      "Their Signal app can help document and file claims quickly",
      "Request a detailed breakdown of the estimate including labor and materials",
      "Farmers often covers code upgrade requirements in Minnesota"
    ]
  },
  "progressive": {
    name: "Progressive",
    claimsUrl: "https://www.progressive.com/claims/",
    claimsPhone: "1-800-776-4737",
    description: "Progressive has expanded their homeowner insurance offerings in Minnesota, often bundled with auto policies.",
    tips: [
      "Progressive's online claims center provides 24/7 claim filing",
      "They often provide competitive rates for bundled home and auto policies",
      "Request itemized estimates for all repairs",
      "Progressive typically processes storm claims within 2-3 weeks"
    ]
  },
  "liberty-mutual": {
    name: "Liberty Mutual",
    claimsUrl: "https://www.libertymutual.com/claims",
    claimsPhone: "1-800-225-2467",
    description: "Liberty Mutual offers customizable homeowner policies in Minnesota with various coverage levels and deductible options.",
    tips: [
      "Liberty Mutual's mobile app allows photo documentation during claims",
      "They offer both ACV and replacement cost coverage - verify your policy type",
      "Ask about their Better Roof Replacement coverage for full replacement",
      "Liberty Mutual often allows direct contractor payments"
    ]
  },
  "travelers": {
    name: "Travelers",
    claimsUrl: "https://www.travelers.com/claims",
    claimsPhone: "1-800-252-4633",
    description: "Travelers is one of the oldest insurance companies in America, offering comprehensive homeowner protection in Minnesota.",
    tips: [
      "Travelers offers specialized coverage for older Minnesota homes",
      "Their claims process is known for being thorough and detailed",
      "Request a meeting with the adjuster to walk through all damage",
      "Travelers often covers ice dam damage common in Minnesota"
    ]
  },
  "usaa": {
    name: "USAA",
    claimsUrl: "https://www.usaa.com/inet/wc/claims-center",
    claimsPhone: "1-800-531-8722",
    description: "USAA serves military members and their families with highly-rated homeowner insurance coverage in Minnesota.",
    tips: [
      "USAA consistently ranks highest in customer satisfaction for claims",
      "Their mobile app provides excellent claims tracking features",
      "USAA often processes claims faster than industry average",
      "Military families can access claims support 24/7"
    ]
  },
  "nationwide": {
    name: "Nationwide",
    claimsUrl: "https://www.nationwide.com/claims/",
    claimsPhone: "1-800-421-3535",
    description: "Nationwide offers comprehensive homeowner insurance in Minnesota with their 'On Your Side' claims service commitment.",
    tips: [
      "Nationwide's On Your Side Review helps ensure you have adequate coverage",
      "Their claims app allows real-time tracking of your claim status",
      "Nationwide offers Brand New Belongings coverage for personal property",
      "Ask about their vanishing deductible program for claim-free years"
    ]
  },
  "auto-owners": {
    name: "Auto-Owners Insurance",
    claimsUrl: "https://www.auto-owners.com/claims",
    claimsPhone: "1-800-346-0346",
    description: "Auto-Owners Insurance is a regional carrier with strong presence in the Midwest, known for personalized service through independent agents.",
    tips: [
      "Auto-Owners works exclusively through independent agents who advocate for you",
      "They're known for fair claim settlements and personalized service",
      "Claims are typically handled locally rather than through a national call center",
      "Auto-Owners often provides quick turnaround on storm damage claims"
    ]
  },
  "western-national": {
    name: "Western National Insurance",
    claimsUrl: "https://www.wnins.com/claims",
    claimsPhone: "1-800-862-6070",
    description: "Western National is a regional insurer headquartered in Minnesota, with deep understanding of local weather challenges.",
    tips: [
      "As a Minnesota-based company, Western National understands local storm patterns",
      "They work through independent agents who know your community",
      "Western National is known for responsive local claims handling",
      "Their adjusters are familiar with Minnesota building codes and requirements"
    ]
  },
  "farm-bureau": {
    name: "Farm Bureau Financial Services",
    claimsUrl: "https://www.fbfs.com/claims",
    claimsPhone: "1-800-831-7587",
    description: "Farm Bureau serves Minnesota homeowners, particularly in rural areas, with comprehensive property coverage.",
    tips: [
      "Farm Bureau agents often have deep roots in local Minnesota communities",
      "They offer specialized coverage for rural properties and outbuildings",
      "Farm Bureau is known for personalized claims service",
      "Their adjusters understand agricultural and rural property needs"
    ]
  },
  "safeco": {
    name: "Safeco Insurance",
    claimsUrl: "https://www.safeco.com/claims",
    claimsPhone: "1-800-332-3226",
    description: "Safeco, a Liberty Mutual company, offers homeowner insurance in Minnesota through independent agents.",
    tips: [
      "Safeco offers RightTrack discount program for qualifying customers",
      "They provide coverage options for both standard and unique homes",
      "Safeco's claims process integrates with Liberty Mutual resources",
      "Ask about their Safeco Deductible Savings program"
    ]
  },
  "hartford": {
    name: "The Hartford",
    claimsUrl: "https://www.thehartford.com/claims",
    claimsPhone: "1-800-243-5860",
    description: "The Hartford provides homeowner insurance with a focus on service and has been in business for over 200 years.",
    tips: [
      "The Hartford is known for straightforward claims processing",
      "They offer AARP members special homeowner insurance programs",
      "Their Claim Concierge service helps guide you through the process",
      "The Hartford provides 24/7 claims reporting options"
    ]
  },
  "chubb": {
    name: "Chubb Insurance",
    claimsUrl: "https://www.chubb.com/us-en/claims.html",
    claimsPhone: "1-800-252-4670",
    description: "Chubb provides premium homeowner insurance for high-value homes in Minnesota with exceptional claims service.",
    tips: [
      "Chubb specializes in coverage for high-value and luxury homes",
      "They offer guaranteed replacement cost coverage",
      "Chubb's Masterpiece policy includes extended replacement cost",
      "Their claims service is known for white-glove treatment"
    ]
  }
};

// Default data for carriers not in the config
const DEFAULT_CARRIER_DATA = {
  tips: [
    "Contact your insurance company's claims line directly to start the process",
    "Document all damage with photos and videos before any repairs",
    "Keep records of all communication with your insurance company",
    "Get multiple contractor estimates to compare with the insurance estimate"
  ]
};

export default function CarrierClaimsPage({ carrierSlug }) {
  useSEO();
  
  const carrier = CARRIER_DATA[carrierSlug] || {
    name: carrierSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    ...DEFAULT_CARRIER_DATA
  };

  const claimSteps = [
    {
      title: "Document Everything",
      description: `Before contacting ${carrier.name}, document all visible storm damage. Take photos of your roof from ground level, any interior water damage, and damaged gutters or siding.`,
      icon: Camera
    },
    {
      title: `Contact ${carrier.name}`,
      description: carrier.claimsPhone 
        ? `Call ${carrier.name} at ${carrier.claimsPhone} or visit their online claims portal to file your claim.`
        : `Contact ${carrier.name} through their claims portal or call their main customer service line to begin your claim.`,
      icon: Phone
    },
    {
      title: "Get a Professional Inspection",
      description: "Schedule a free roof inspection with StormCare. We'll provide detailed documentation and photos that can support your insurance claim.",
      icon: FileText
    },
    {
      title: "Meet the Adjuster",
      description: "When the insurance adjuster visits, be present and walk them through all damage. Share your professional inspection report.",
      icon: Users
    },
    {
      title: "Review the Estimate",
      description: "Compare the insurance estimate with your contractor's assessment. If there are discrepancies, request a re-inspection or supplement.",
      icon: CheckCircle
    }
  ];

  const mnConsiderations = [
    "Minnesota's freeze-thaw cycles can cause hidden damage that may not be immediately visible",
    "Hail damage is common April through September - document dents on soft metals as evidence",
    "Ice dam damage may be covered under your policy - check your specific coverage",
    "Minnesota law allows you to choose your own contractor regardless of insurance recommendations",
    "Most Minnesota policies require claims to be filed within 60 days of damage discovery"
  ];

  return (
    <div data-testid={`carrier-claims-${carrierSlug}`} className="">
      {/* Hero Section */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Shield className="w-4 h-4" />
              {carrier.name} Claims Guide
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-tight mb-6">
              How to File a Roof Damage Claim with {carrier.name}
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              {carrier.description || `Everything Minnesota homeowners need to know about filing storm damage claims with ${carrier.name}.`}
            </p>

            <div className="flex flex-wrap gap-4">
              {carrier.claimsUrl && (
                <a href={carrier.claimsUrl} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-orange hover:bg-orange-hover text-white px-6 py-4 text-lg">
                    Start {carrier.name} Claim
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
              <Link to="/#get-started">
                <Button variant="outline" className="border-gray-600 text-white hover:bg-white/10 px-6 py-4 text-lg">
                  Get Free Inspection First
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      {carrier.claimsPhone && (
        <section className="bg-orange py-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-6 text-white">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span className="font-medium">{carrier.name} Claims:</span>
                <a href={`tel:${carrier.claimsPhone.replace(/-/g, '')}`} className="underline">
                  {carrier.claimsPhone}
                </a>
              </div>
              {carrier.claimsUrl && (
                <a 
                  href={carrier.claimsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 underline"
                >
                  Online Claims Portal
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Claim Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Step-by-Step: Filing Your {carrier.name} Claim
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Follow these steps to successfully file your roof damage claim
            </p>
          </div>

          <div className="space-y-6">
            {claimSteps.map((step, idx) => (
              <div 
                key={step.title}
                className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex items-start gap-6"
              >
                <div className="w-12 h-12 bg-orange rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-heading font-bold text-white">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-semibold text-navy mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                <step.icon className="w-8 h-8 text-gray-300 hidden md:block flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Carrier-Specific Tips */}
      {carrier.tips && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-6">
                  {carrier.name} Claim Tips
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  Here are some specific tips for working with {carrier.name} on your storm damage claim:
                </p>
                
                <div className="space-y-4">
                  {carrier.tips.map((tip) => (
                    <div key={tip} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-orange flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-navy rounded-2xl p-8">
                <h3 className="font-heading text-xl font-semibold text-white mb-4">
                  Minnesota-Specific Considerations
                </h3>
                <div className="space-y-4">
                  {mnConsiderations.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-orange flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Documentation Needed */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Documentation Needed for Your Claim
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Having proper documentation can significantly speed up your {carrier.name} claim
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Camera,
                title: "Photos & Videos",
                items: ["Roof damage from ground level", "Close-ups of damaged areas", "Interior water stains", "Damaged gutters/siding"]
              },
              {
                icon: FileText,
                title: "Documents",
                items: ["Your insurance policy", "Previous inspection reports", "Contractor estimates", "Weather reports"]
              },
              {
                icon: Clock,
                title: "Timeline",
                items: ["Date of storm event", "When damage was discovered", "Dates of any repairs", "Communication log"]
              },
              {
                icon: Users,
                title: "Professional Reports",
                items: ["Roof inspection report", "Damage assessment", "Repair estimates", "Material specifications"]
              }
            ].map((category) => (
              <div key={category.title} className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-orange/10 rounded-xl flex items-center justify-center mb-4">
                  <category.icon className="w-6 h-6 text-orange" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.items.map((item) => (
                    <li key={item} className="text-sm text-gray-600 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-orange rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-4">
            Need Help With Your {carrier.name} Claim?
          </h2>
          
          <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            StormCare provides free roof inspections for Minnesota homeowners. We'll document any 
            storm damage and provide the detailed reports you need to support your {carrier.name} claim.
          </p>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-300 mb-10">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              Free Inspection
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              Detailed Documentation
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              Adjuster Meeting Support
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#get-started">
              <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-6 text-lg w-full sm:w-auto">
                <Zap className="w-5 h-5 mr-2" />
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+1-555-STORM-MN">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto">
                <Phone className="w-5 h-5 mr-2" />
                Call StormCare Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-semibold text-navy mb-8 text-center">
            More Insurance Claim Resources
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link 
              to="/insurance-claims"
              className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange transition-all group"
            >
              <h3 className="font-medium text-navy group-hover:text-orange mb-2">
                Insurance Claims Hub
              </h3>
              <p className="text-sm text-gray-500">
                Complete guide to storm damage claims
              </p>
            </Link>
            <Link 
              to="/guides"
              className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange transition-all group"
            >
              <h3 className="font-medium text-navy group-hover:text-orange mb-2">
                Free Guides
              </h3>
              <p className="text-sm text-gray-500">
                Download insurance claim survival guide
              </p>
            </Link>
            <Link 
              to="/faqs"
              className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange transition-all group"
            >
              <h3 className="font-medium text-navy group-hover:text-orange mb-2">
                Insurance FAQs
              </h3>
              <p className="text-sm text-gray-500">
                Common questions about claims
              </p>
            </Link>
            <Link 
              to="/insurance-agents"
              className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange transition-all group"
            >
              <h3 className="font-medium text-navy group-hover:text-orange mb-2">
                Find an Agent
              </h3>
              <p className="text-sm text-gray-500">
                Connect with local insurance agents
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-xs text-gray-500 text-center">
            <strong>Disclaimer:</strong> StormCare is not affiliated with {carrier.name} and is not a public 
            insurance adjuster. We do not determine insurance coverage, claim approval, or claim value. 
            Coverage decisions are made solely by your insurance carrier according to the terms and 
            conditions of your policy.
          </p>
        </div>
      </section>
    </div>
  );
}
